<template>
  <div>
    <div class="tw:my-3 tw:flex tw:items-center tw:justify-between tw:gap-3">
      <h5 id="extra-fields" class="tw:m-0 tw:text-base tw:font-semibold">
        <a href="#extra-fields" class="tw:text-foreground tw:no-underline">
          {{ $t('Registration custom fields') }}
        </a>
      </h5>

      <Button type="button" variant="outline" size="sm" @click.prevent="onClickAddField()">
        <CirclePlusIcon class="tw:size-4" />
        {{ $t('Add new field') }}
      </Button>
    </div>

    <div v-if="list.length === 0" class="tw:text-muted-foreground">
      {{ $t('You haven’t created custom fields yet.') }}<br>
      {{ $t('Custom fields allow you to collect additional information during registration.') }}
    </div>

    <div class="tw:divide-y tw:divide-gray-200 tw:border-y tw:border-gray-200">
      <div
        v-for="(field, i) in list"
        :key="field.name"
        class="tw:py-3"
      >
        <div class="tw:flex tw:items-start tw:gap-3">
          <div class="tw:shrink-0 tw:mt-1 tw:text-muted-foreground">
              <svg v-if="field.type === 'select'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16"><path fill="currentColor" d="M5.75 7.5h7.5a.75.75 0 0 1 0 1.5h-7.5a.75.75 0 0 1 0-1.5Zm0 5h7.5a.75.75 0 0 1 0 1.5h-7.5a.75.75 0 0 1 0-1.5Zm-4-10h6.5a.75.75 0 0 1 0 1.5h-6.5a.75.75 0 0 1 0-1.5ZM2 14a1 1 0 1 1 0-2a1 1 0 0 1 0 2Zm1-6a1 1 0 1 1-2 0a1 1 0 0 1 2 0Zm10.314-3.082L11.07 2.417A.25.25 0 0 1 11.256 2h4.488a.25.25 0 0 1 .186.417l-2.244 2.5a.25.25 0 0 1-.372 0Z"/></svg>
              <svg v-if="field.type === 'text'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 48 48"><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="4" d="M6 33v6a3 3 0 0 0 3 3h6m18 0h6a3 3 0 0 0 3-3v-6m0-18V9a3 3 0 0 0-3-3h-6M6 15V9a3 3 0 0 1 3-3h6m9 9v20m-7-20h14"/></svg>
              <svg v-if="field.type === 'simple-checkbox'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M17.28 9.28a.75.75 0 0 0-1.06-1.06l-5.97 5.97l-2.47-2.47a.75.75 0 0 0-1.06 1.06l3 3a.75.75 0 0 0 1.06 0l6.5-6.5Z"/><path fill="currentColor" d="M3.75 2h16.5c.966 0 1.75.784 1.75 1.75v16.5A1.75 1.75 0 0 1 20.25 22H3.75A1.75 1.75 0 0 1 2 20.25V3.75C2 2.784 2.784 2 3.75 2ZM3.5 3.75v16.5c0 .138.112.25.25.25h16.5a.25.25 0 0 0 .25-.25V3.75a.25.25 0 0 0-.25-.25H3.75a.25.25 0 0 0-.25.25Z"/></svg>
              <svg v-if="field.type === 'checkbox'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M21 19v-2H8v2h13m0-6v-2H8v2h13M8 7h13V5H8v2M4 5v2h2V5H4M3 5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5m1 6v2h2v-2H4m-1 0a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-2m1 6v2h2v-2H4m-1 0a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-2Z"/></svg>
              <svg v-if="field.type === 'signature'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 256 256"><path fill="currentColor" d="M232 164H70.35c2.2-4.42 4.41-8.94 6.59-13.53c.24-.49.47-1 .7-1.46c13.56-1.6 28.78-11.26 45.52-28.9c5.33 12.06 14.07 24.87 27.65 27.44c5.25 1 13.49.79 22.9-5.81a57.35 57.35 0 0 0 9.08-8.06C191.12 141.3 205.87 148 232 148a12 12 0 0 0 0-24c-32.66 0-35.81-11.61-36-12.6a12 12 0 0 0-23.06-4c-9.37 13.93-15.6 17-17.66 16.6c-4.91-.93-12.45-15.7-15.52-30.39a12 12 0 0 0-22.09-3.68c-10 12.38-18.56 20.93-25.68 26.5c21.31-53.19 17.15-72.52 11.47-82.57c-5-8.87-14-13.78-25.36-13.83h-.32c-18 .13-32.43 16.66-38.73 44.27c-3.45 15.13-4 31.88-1.46 45.94c2.64 14.69 8.33 25.67 16.61 32.22c-3.57 7.38-7.2 14.63-10.75 21.54H24a12 12 0 0 0 0 24h6.68c-9.68 17.67-16.84 29.6-17 29.81a12 12 0 0 0 4.1 16.47A11.85 11.85 0 0 0 24 236a12 12 0 0 0 10.29-5.81C34.91 229.14 45.22 212 58 188h174a12 12 0 0 0 0-24Zm-36-52v-.54a3.19 3.19 0 0 1 0 .54ZM62.44 69.61C65.77 55 72.44 44 78 44c3.67 0 4.25 1 4.6 1.64c1.43 2.54 6.55 17.09-17.42 72.8A44.79 44.79 0 0 1 61.2 106a94.89 94.89 0 0 1 1.24-36.39Z"/></svg>
          </div>

          <div class="tw:flex tw:flex-1 tw:flex-col tw:min-w-0 tw:gap-2">
            <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
              <strong class="tw:truncate">{{ field.name }}</strong>
              <span v-if="['select', 'checkbox'].includes(field.type) && (!Array.isArray(field.choices) || field.choices.length === 0)">
                <Badge variant="red">{{ $t('Choices not set') }}</Badge>
              </span>
              <span v-else-if="field.choices && Array.isArray(field.choices)">
                <Badge variant="zinc-light">
                  {{ $t('{count} choice(s)', { count: field.choices.length }) }}
                </Badge>
              </span>
            </div>

            <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-1">
              <LocaleStatusBadge
                v-for="locale in locales"
                :key="locale.value"
                size="sm"
                :locale="locale"
                :completed="!!field.translations.find(translation => translation.locale === locale.value)?.value"
              />
            </div>
          </div>

          <div class="tw:shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger as-child>
                <Button variant="outline" size="sm">
                  {{ $t('Actions') }}
                  <ChevronDownIcon class="tw:ml-2 tw:h-4 tw:w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem @click="onClickEditField(field, i)">
                  <PenIcon class="tw:size-4" />
                  {{ $t('Edit') }}
                </DropdownMenuItem>
                <DropdownMenuItem @click="onDuplicateField(field)">
                  <CopyIcon class="tw:size-4" />
                  {{ $t('Duplicate') }}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive" @click="onRequestDeleteField(i)">
                  <Trash2Icon class="tw:size-4" />
                  {{ $t('Delete') }}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </div>

    <Alert v-if="hasChanged" variant="warning" class="tw:mt-2">
      <strong>{{ $t('Changes detected') }}</strong>
      <p class="tw:m-0">{{ $t('To validate your modification, save the configuration at the bottom of the page') }}</p>
    </Alert>

    <input type="hidden" :name="name" :value="fieldsConfigJson">

    <ExtrafieldModal
      v-model:open="showExtrafieldModal"
      :field="fieldToEdit"
      :locales="locales"
      :default-locale="defaultLocale"
      :types-available="typesAvailable"
      :existing-field-names="existingFieldNames"
      @save="onSaveField"
    />

    <!-- Delete field confirmation dialog -->
    <AlertDialog :open="showDeleteFieldDialog" @openChange="showDeleteFieldDialog = $event">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{{ $t('Confirm deletion') }}</AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogDescription>
          {{ $t('Are you sure you want to delete this field?') }}<br/>
          {{ $t('This action cannot be undone.') }}
        </AlertDialogDescription>
        <AlertDialogFooter>
          <AlertDialogCancel as-child>
            <Button variant="secondary" @click="showDeleteFieldDialog = false">
              {{ $t('Cancel') }}
            </Button>
          </AlertDialogCancel>
          <AlertDialogAction  variant="destructive" @click.prevent="onDeleteField">
            {{ $t('Delete') }}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
</template>

<script setup>
import ExtrafieldModal from "./ExtrafieldModal.vue";
import { ref, computed, onMounted } from "vue";
import Alert from "@/components/ui/Alert.vue";
import LocaleStatusBadge from "@/components/ui/LocaleStatusBadge.vue";
import { Button } from "@/shadcn/components/ui/button/index.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/shadcn/components/ui/dropdown-menu/index.js";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shadcn/components/ui/alert-dialog";
import { ChevronDownIcon, CirclePlusIcon, CopyIcon, PenIcon, Trash2Icon } from "@lucide/vue";
import { Badge } from "@/components/ui/badge";
import { makeUniqueName } from "@/utils/string";

const props = defineProps({
  name: {
    type: String,
    required: true
  },
  fields: {
    type: Array,
    required: true
  },
  locales: {
    type: Array,
    required: true
  },
  defaultLocale: {
    type: String,
    required: true
  },
  typesAvailable: {
    type: Array,
    required: true
  }
})

const list = ref([])
const showExtrafieldModal = ref(false)
const fieldToEdit = ref({})
const fieldToEditIndex = ref(-1)

// Delete field confirmation
const showDeleteFieldDialog = ref(false)
const fieldToDeleteIndex = ref(-1)

const fieldsConfigJson = computed(() => {
  return JSON.stringify(list.value);
})

const hasChanged = computed(() => {
  return JSON.stringify(list.value) !== JSON.stringify(props.fields);
})

// List of existing field names (to ensure uniqueness)
// Exclude the field being edited if in edit mode
const existingFieldNames = computed(() => {
  return list.value
    .filter((_, index) => index !== fieldToEditIndex.value)
    .map(f => f.name)
    .filter(name => name); // Exclude empty names
})

const onSaveField = (updatedField) => {
  if (fieldToEditIndex.value > -1) {
    // Update existing field
    list.value.splice(fieldToEditIndex.value, 1, updatedField);
  } else {
    // Add new field
    list.value.push(updatedField);
  }
}

const onClickEditField = (field, index) => {
  fieldToEdit.value = JSON.parse(JSON.stringify(field))
  fieldToEditIndex.value = index;
  showExtrafieldModal.value = true;
}

const onClickAddField = () => {
  fieldToEdit.value = {};
  fieldToEditIndex.value = -1;
  showExtrafieldModal.value = true;
}

const onDuplicateField = (field) => {
  // Clone the field with all its parameters
  const duplicatedField = JSON.parse(JSON.stringify(field));

  // Generate a unique name for the duplicated field
  const allExistingNames = list.value.map(f => f.name).filter(name => name);
  duplicatedField.name = makeUniqueName(field.name, allExistingNames);

  // Add the duplicated field to the list
  list.value.push(duplicatedField);
}

// Request to delete a field
const onRequestDeleteField = (index) => {
  fieldToDeleteIndex.value = index;
  showDeleteFieldDialog.value = true;
}

// Delete the field
const onDeleteField = () => {
  list.value.splice(fieldToDeleteIndex.value, 1);
  showDeleteFieldDialog.value = false;
}

onMounted(() => {
  list.value = [...props.fields];
})
</script>
